/**
 * Analytics › Audience: who watches and on what. New and returning viewers against the previous
 * period, then devices and countries as ranked bars (groups of fewer than 5 viewers come merged as
 * "Other", so nobody small is singled out).
 */

import { SkeletonCard } from "@web-ui/components/Skeleton";
import { TileGrid } from "../components/tile-grid";
import { analytics, type DimensionRow } from "../api";
import { BarList } from "../charts/bar-list";
import type { Table } from "../csv";
import { countryLabel, definition, platformLabel } from "../definitions";
import { change, count, duration, percent } from "../format";
import { MetricTile } from "../components/metric-tile";
import { NoData, ReportFailed } from "../components/no-data";
import { ReportCard } from "../components/report-card";
import { SampleNotice } from "../components/sample-notice";
import type { ReportScope } from "../scope";
import { useReport } from "../use-report";

export function Audience({ scope }: { scope: ReportScope }) {
  const now = useReport(() => analytics.audience(scope.client, scope.range), [scope.client, scope.range]);
  const before = useReport(() => analytics.audience(scope.client, scope.previous), [scope.client, scope.previous]);
  const a = now.data;

  if (now.failed && !a) return <section className="card"><ReportFailed /></section>;
  if (!a) return <SkeletonCard label="Loading your audience" rows={6} />;
  const b = before.data;
  const nobody = a.newViewers + a.returningViewers === 0 && !a.sample;

  return (
    <div className={`flex flex-col gap-3 transition-opacity ${now.stale ? "opacity-60" : ""}`} aria-busy={now.stale || undefined}>
      <SampleNotice shown={a.sample || !!b?.sample} />
      {nobody ? <section className="card"><NoData /></section> : (
        <>
          <TileGrid columns={2}>
            <MetricTile definition={definition("newViewers", scope.pack)} value={count(a.newViewers)} change={change(a.newViewers, b?.newViewers)} />
            <MetricTile definition={definition("returningViewers", scope.pack)} value={count(a.returningViewers)} change={change(a.returningViewers, b?.returningViewers)} />
          </TileGrid>
          <div className="grid items-start gap-3 xl:grid-cols-2">
            <Breakdown scope={scope} title="Devices" description="Where people watch, by viewers." rows={a.platforms} name={platformLabel} what="devices" />
            <Breakdown scope={scope} title="Countries" description="Where people watch from, by viewers. Countries with fewer than 5 viewers are grouped as Other." rows={a.countries} name={countryLabel} what="countries" />
          </div>
        </>
      )}
    </div>
  );
}

function Breakdown({ scope, title, description, rows, name, what }: { scope: ReportScope; title: string; description: string; rows: DimensionRow[]; name: (key: string) => string; what: string }) {
  const sorted = [...rows].sort((x, y) => (x.key === "other" ? 1 : y.key === "other" ? -1 : y.viewers - x.viewers));
  const total = rows.reduce((sum, r) => sum + r.viewers, 0);
  const d = (id: "viewers" | "plays" | "watchSeconds") => definition(id, scope.pack);
  const table: Table<DimensionRow> = {
    columns: [
      { label: title === "Devices" ? "Device" : "Country", value: (r) => name(r.key) },
      { label: "Key", value: (r) => r.key, csvOnly: true },
      { label: d("viewers").label, tooltip: d("viewers").tooltip, value: (r) => r.viewers, format: (v) => count(Number(v)), numeric: true },
      { label: d("plays").label, tooltip: d("plays").tooltip, value: (r) => r.plays, format: (v) => count(Number(v)), numeric: true },
      { label: d("watchSeconds").label, tooltip: d("watchSeconds").tooltip, csvLabel: "Watch time (seconds)", value: (r) => r.watchSeconds, format: (v) => duration(Number(v)), numeric: true },
    ],
    rows: sorted,
  };
  return (
    <ReportCard title={title} description={description} table={table} file={{ brand: scope.brand, what, range: scope.range }}
      empty={rows.length === 0 ? <NoData title="Nothing to show yet" /> : undefined}>
      <BarList label={title} bars={sorted.map((r) => ({ key: r.key, label: name(r.key), value: r.viewers, text: count(r.viewers), detail: `${percent(total ? r.viewers / total : null)} of viewers · ${duration(r.watchSeconds)} watched` }))} />
    </ReportCard>
  );
}
